import React, { Component } from "react";
// @ts-expect-error: no types available for @mapbox/geojson-merge
import { merge } from "@mapbox/geojson-merge";
import { addSeconds, subMinutes } from "date-fns";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { throttle } from "@formant/core/common/throttle";
import { duration } from "@formant/core/model/duration";
import { IGeoJsonIcon } from "@formant/core/model/IGeoJsonIcon";
import { ILocation } from "@formant/core/model/ILocation";
import { ILocationModuleParameters } from "@formant/core/model/ILocationModuleParameters";
import {
    Basemap,
    ILocationViewConfiguration,
    ILocationViewport,
} from "@formant/core/model/ILocationViewConfiguration";
import { IStreamData } from "@formant/core/model/IStreamData";
import { IStreamTypeMap } from "@formant/core/model/IStreamTypeMap";

import { queryClient } from "../../../clients";
import { appStore, authStore, observeStore, queryStore } from "../../../stores";
import { freeWebglContext, getWebglContext } from "../../../utils/webglCheck";
import { copyToClipboard } from "../../common/copyToClipboard";
import { WebglError } from "../../common/WebglError";
import { Lazy } from "../../Lazy";
import { IBaseModuleProps } from "../IBaseModuleProps";
import { Module } from "../Module";
import { findConfiguration } from "../utils/findConfiguration";
import { BasemapSelector } from "./BasemapSelector";
import * as styles from "./index.scss";

const LocationMap = Lazy(
    async () => (await import("./LocationMap")).LocationMap,
);

@observer
export class LocationModule extends Component<IBaseModuleProps> {
    @observable
    private basemap: Basemap = "satellite";
    private viewport?: ILocationViewport;
    private data: IStreamData<"location">[] = [];
    @observable
    private mergedGeoJSON: any | undefined;
    private hasWebglContext = false;

    public componentDidMount() {
        this.hasWebglContext = getWebglContext();

        const { streamName, configuration } = this.props;

        const { basemap, viewport } = findConfiguration(
            configuration,
            streamName,
            "location",
        ) || {
            basemap: undefined,
        };
        if (basemap) {
            this.basemap = basemap;
        }
        this.viewport = viewport;

        appStore.addCustomMenuHandler(
            streamName,
            "location-google-maps",
            this.openInGoogleMaps,
        );

        appStore.addCustomMenuHandler(
            streamName,
            "location-clipboard",
            this.copyLatLng,
        );
    }

    public componentWillUnmount() {
        const { streamName } = this.props;
        appStore.removeCustomMenuHandler(
            streamName,
            "location-google-maps",
            this.openInGoogleMaps,
        );
        appStore.removeCustomMenuHandler(
            streamName,
            "location-clipboard",
            this.copyLatLng,
        );
        if (this.hasWebglContext) {
            freeWebglContext();
        }
    }

    private get firstLocation(): ILocation | undefined {
        const queryData = this.getCurrentData();
        if (
            queryData !== "too much data" &&
            queryData !== undefined &&
            queryData.length > 0
        ) {
            return queryData[0].points[0][1];
        } else {
            return undefined;
        }
    }

    public openInGoogleMaps = () => {
        const { firstLocation } = this;
        if (firstLocation) {
            window.open(
                `https://www.google.com/maps/dir/?api=1&destination=${firstLocation.latitude},${firstLocation.longitude}`,
                "_blank",
            );
        } else {
            appStore.setSnackbar("No location data to open in Google Maps.");
        }
    };

    private copyLatLng = async () => {
        const { firstLocation } = this;
        if (firstLocation) {
            const { latitude, longitude } = firstLocation;
            const latLngString = `${latitude}, ${longitude}`;
            try {
                copyToClipboard(latLngString);
                appStore.setSnackbar(
                    `Location (${latLngString}) copied to clipboard`,
                );
            } catch (_) {
                return;
            }
        }
    };

    private getCurrentData() {
        const { streamName, filter } = this.props;
        const { time } = observeStore;

        const startTime = subMinutes(time, 1);
        const endTime = addSeconds(time, 1);

        return queryStore.moduleQuery(
            filter,
            streamName,
            "location",
            startTime,
            endTime,
        );
    }

    private updateGeoJsonLayers = throttle(async () => {
        const latest = await this.getLatestGeoJson();
        if (latest.length > 0) {
            // filter repeated streams by keeping the latest one
            // this happens when datapoints have a different set of tags and are treated as different streams
            const filteredLatest = Object.values(
                latest.reduce(
                    (acc, item) => {
                        const name = item.name;
                        if (
                            !acc[name] ||
                            item.points?.[0]?.[0] > acc[name].points?.[0]?.[0]
                        ) {
                            acc[name] = item;
                        }
                        return acc;
                    },
                    {} as Record<string, IStreamData<keyof IStreamTypeMap>>,
                ),
            );
            const allGeoJsons = filteredLatest.reduce<any[]>((acc, curr) => {
                // non-assetized data points will have an embedded object
                const geoJsons = curr.points
                    .filter(point => point[1].toString()[0] === "{")
                    .map(_ => JSON.parse(_[1] as string));
                return [...acc, ...geoJsons];
            }, []);
            const allGeoJsonUrls = latest.reduce<any[]>((acc, curr) => {
                // assetized data points will have a URL instead of an object
                const geoJsonAssets = curr.points
                    .filter(point => point[1].toString()[0] !== "{")
                    .map(_ => _[1]);
                return [...acc, ...geoJsonAssets];
            }, []);
            const newJsonRequests = await Promise.all(
                allGeoJsonUrls.map(_ => fetch(_)),
            );
            const allGeoJsonResponses = await Promise.all(
                newJsonRequests.map(_ => _.json()),
            );
            this.mergedGeoJSON = merge([
                ...allGeoJsons,
                ...allGeoJsonResponses,
            ]);
        }
    }, 1 * duration.second);

    private async getLatestGeoJson(): Promise<
        IStreamData<keyof IStreamTypeMap>[]
    > {
        const { layoutModule } = this.props;
        if (layoutModule?.configuration) {
            const configuration = layoutModule.configuration;
            if (configuration.moduleType === "location") {
                const parameters = configuration.parameters as
                    | ILocationModuleParameters
                    | undefined;
                if (parameters && this.props.device?.id) {
                    const start = (
                        observeStore.range[0] || new Date()
                    ).toISOString();
                    const geoJsonLayers = parameters.geoJsonLayers;
                    const streamIds = geoJsonLayers.map(_ => _.streamId);
                    const token = await authStore.token();
                    return await queryClient.query(token, {
                        deviceIds: [this.props.device?.id],
                        names: streamIds,
                        latestOnly: true,
                        start,
                        end: observeStore.time.toISOString(),
                    });
                }
                return [];
            }
        }
        return [];
    }

    public render() {
        const { focused, layoutModule } = this.props;

        const queryData = this.getCurrentData();
        this.updateGeoJsonLayers();

        const loading = queryData === undefined;
        const tooMuchData = queryData === "too much data";

        this.data = (queryData !== "too much data" && queryData) || this.data;
        const data = this.data;

        const empty = data.length === 0;

        let icons: IGeoJsonIcon[] = [];
        if (layoutModule?.configuration) {
            const configuration = layoutModule.configuration;
            if (configuration.moduleType === "location") {
                const parameters = configuration.parameters as
                    | ILocationModuleParameters
                    | undefined;
                icons = parameters?.geoJsonIcons || [];
            }
        }

        return (
            <Module
                {...this.props}
                loading={loading}
                empty={empty}
                tooMuchData={tooMuchData}
                type="location"
                className={styles["location-module"]}
            >
                {this.hasWebglContext && (
                    <LocationMap
                        data={data}
                        additionalGeoJson={this.mergedGeoJSON}
                        geoJsonIcons={icons}
                        basemap={this.basemap}
                        unfocused={!focused}
                        viewport={this.viewport}
                        onChangeViewport={this.onChangeViewport}
                    />
                )}
                <div className={styles.configuration}>
                    <BasemapSelector
                        basemap={this.basemap}
                        onChange={this.onChangeBasemap}
                    />
                </div>
                <WebglError visible={!this.hasWebglContext} />
            </Module>
        );
    }

    private onChangeBasemap = (basemap: Basemap) => {
        this.basemap = basemap;

        this.changeConfiguration({ basemap });
    };

    private onChangeViewport = (viewport: ILocationViewport | undefined) => {
        this.changeConfiguration({ viewport });
    };

    private changeConfiguration(updates: Partial<ILocationViewConfiguration>) {
        const { streamName, onChangeConfiguration } = this.props;

        if (!onChangeConfiguration) {
            return;
        }

        onChangeConfiguration({
            streamName,
            type: "location",
            updates,
        });
    }
}
